import {
  formatHumanName,
  getStatus,
  OperationOutcomeError,
  type MedplumClient,
  type MedplumRequestOptions,
} from '@medplum/core'
import type { Coverage, Practitioner, QuestionnaireResponse } from '@medplum/fhirtypes'
import { ZOCDOC_INSURANCE_REQUEST_SOURCE } from '@/model/middleware-types'

export const INSURANCE_ORG_DECISION_QUESTIONNAIRE =
  'https://behuman.health/questionnaire/insurance-org-decision'
export const PATIENT_INSURANCE_REQUEST_QUESTIONNAIRE =
  'https://behuman.health/questionnaire/patient-insurance-request'

type InsuranceDecisionMode = 'approve' | 'disapprove'

type InsuranceOrgDecisionInput = {
  patientId: string
  practitioner: Practitioner
  organizationId: string
  coverage: Coverage
  mode: InsuranceDecisionMode
  reason: string
  notes: string
  encounterId?: string
}

type PatientInsuranceRequestInput = {
  patientId: string
  appointmentId?: string
  encounterId?: string
  coverageId?: string
  payerName: string
  type: string
  insuranceNumber?: string
  subscriberName?: string
  subscriberDob?: string
  subscriberMemberId?: string
  frontInsuranceCardDocumentReference?: string
  backInsuranceCardDocumentReference?: string
}

export type InsuranceOrgDecision = {
  id: string
  mode: InsuranceDecisionMode
  reason: string
  notes: string
  reviewedBy: string
  reviewedOn: string
  coverageReference: string
  organizationReference: string
  questionnaireResponse: QuestionnaireResponse
}

export type PatientInsuranceRequest = {
  id: string
  status?: QuestionnaireResponse['status']
  authored?: string
  payerName: string
  payerId?: string
  type: string
  insuranceNumber?: string
  subscriberName?: string
  subscriberDob?: string
  subscriberMemberId?: string
  appointmentId?: string
  encounterId?: string
  coverageId?: string
  frontInsuranceCardDocumentReference?: string
  backInsuranceCardDocumentReference?: string
  /** Only on requests filed from a Zocdoc booking — the portal asks for no group number. */
  groupNumber?: string
  /** Filed from a Zocdoc booking's insurance rather than typed by the patient. */
  fromZocdoc: boolean
  questionnaireResponse: QuestionnaireResponse
}

function buildStringItem(linkId: string, value?: string) {
  if (!value) {
    return undefined
  }

  return {
    linkId,
    answer: [{ valueString: value }],
  }
}

function readString(response: QuestionnaireResponse, linkId: string): string | undefined {
  return response.item?.find((item) => item.linkId === linkId)?.answer?.[0]?.valueString
}

export function buildInsuranceOrgDecisionQuestionnaireResponse(
  input: InsuranceOrgDecisionInput,
): QuestionnaireResponse {
  const practitionerName = formatHumanName(input.practitioner.name?.[0]) || 'Unknown reviewer'
  const coverageReference = `Coverage/${input.coverage.id}`
  const organizationReference = `Organization/${input.organizationId}`

  return {
    resourceType: 'QuestionnaireResponse',
    status: 'completed',
    questionnaire: INSURANCE_ORG_DECISION_QUESTIONNAIRE,
    subject: { reference: `Patient/${input.patientId}` },
    encounter: input.encounterId ? { reference: `Encounter/${input.encounterId}` } : undefined,
    author: {
      reference: `Practitioner/${input.practitioner.id}`,
      display: practitionerName,
    },
    authored: new Date().toISOString(),
    item: [
      buildStringItem('mode', input.mode),
      buildStringItem('reason', input.reason),
      buildStringItem('notes', input.notes),
      buildStringItem('reviewedBy', practitionerName),
      buildStringItem('coverageReference', coverageReference),
      buildStringItem('organizationReference', organizationReference),
      buildStringItem('coverageVersionId', input.coverage.meta?.versionId),
    ].filter((item): item is NonNullable<typeof item> => item !== undefined),
    meta: {
      source: 'insurance-org-decision',
      compartment: [
        { reference: `Patient/${input.patientId}` },
        { reference: organizationReference },
      ],
    },
  }
}

export function buildPatientInsuranceRequestQuestionnaireResponse(
  input: PatientInsuranceRequestInput,
): QuestionnaireResponse {
  return {
    resourceType: 'QuestionnaireResponse',
    status: 'completed',
    questionnaire: PATIENT_INSURANCE_REQUEST_QUESTIONNAIRE,
    subject: { reference: `Patient/${input.patientId}` },
    encounter: input.encounterId ? { reference: `Encounter/${input.encounterId}` } : undefined,
    authored: new Date().toISOString(),
    item: [
      buildStringItem('payerName', input.payerName),
      buildStringItem('type', input.type),
      buildStringItem('insuranceNumber', input.insuranceNumber),
      buildStringItem('subscriberName', input.subscriberName),
      buildStringItem('subscriberDob', input.subscriberDob),
      buildStringItem('subscriberMemberId', input.subscriberMemberId),
      buildStringItem('appointmentId', input.appointmentId),
      buildStringItem('encounterId', input.encounterId),
      buildStringItem('coverageId', input.coverageId),
      buildStringItem(
        'frontInsuranceCardDocumentReference',
        input.frontInsuranceCardDocumentReference,
      ),
      buildStringItem(
        'backInsuranceCardDocumentReference',
        input.backInsuranceCardDocumentReference,
      ),
    ].filter((item): item is NonNullable<typeof item> => item !== undefined),
    meta: {
      source: 'patient-insurance-request',
      compartment: [{ reference: `Patient/${input.patientId}` }],
    },
  }
}

export function parseInsuranceOrgDecision(
  response: QuestionnaireResponse,
): InsuranceOrgDecision | null {
  const coverageReference = readString(response, 'coverageReference')
  const organizationReference = readString(response, 'organizationReference')
  const mode = readString(response, 'mode')

  if (!response.id || !coverageReference || !organizationReference) {
    return null
  }

  if (mode !== 'approve' && mode !== 'disapprove') {
    return null
  }

  return {
    id: response.id,
    mode,
    reason: readString(response, 'reason') || '',
    notes: readString(response, 'notes') || '',
    reviewedBy: readString(response, 'reviewedBy') || response.author?.display || '',
    reviewedOn: response.authored || '',
    coverageReference,
    organizationReference,
    questionnaireResponse: response,
  }
}

export function parsePatientInsuranceRequest(
  response: QuestionnaireResponse,
): PatientInsuranceRequest | null {
  const payerName = readString(response, 'payerName')
  const type = readString(response, 'type')

  if (!response.id || !payerName || !type) {
    return null
  }

  return {
    id: response.id,
    status: response.status,
    authored: response.authored,
    payerName,
    type,
    payerId: readString(response, 'payerId'),
    insuranceNumber: readString(response, 'insuranceNumber'),
    subscriberName: readString(response, 'subscriberName'),
    subscriberDob: readString(response, 'subscriberDob'),
    subscriberMemberId: readString(response, 'subscriberMemberId'),
    appointmentId: readString(response, 'appointmentId'),
    encounterId: readString(response, 'encounterId'),
    coverageId: readString(response, 'coverageId'),
    frontInsuranceCardDocumentReference: readString(
      response,
      'frontInsuranceCardDocumentReference',
    ),
    backInsuranceCardDocumentReference: readString(response, 'backInsuranceCardDocumentReference'),
    groupNumber: readString(response, 'groupNumber'),
    fromZocdoc: response.meta?.source === ZOCDOC_INSURANCE_REQUEST_SOURCE,
    questionnaireResponse: response,
  }
}

export async function fetchLatestInsuranceOrgDecision(
  medplum: MedplumClient,
  input: { patientId: string; coverageId: string; organizationId: string },
): Promise<InsuranceOrgDecision | null> {
  const responses = await medplum.searchResources(
    'QuestionnaireResponse',
    `subject=Patient/${input.patientId}&questionnaire=${encodeURIComponent(
      INSURANCE_ORG_DECISION_QUESTIONNAIRE,
    )}&_sort=-_lastUpdated&_count=50`,
  )

  return (
    responses
      .map(parseInsuranceOrgDecision)
      .filter(Boolean)
      .find(
        (item) =>
          item?.coverageReference === `Coverage/${input.coverageId}` &&
          item.organizationReference === `Organization/${input.organizationId}`,
      ) || null
  )
}

/**
 * When a request was submitted. V1 registration stamps `authored` with the patient's device
 * clock, and a request cannot have been submitted after the server last saved it, so a clock
 * that runs fast is capped at `meta.lastUpdated`.
 */
export function insuranceRequestSubmittedAt(response: QuestionnaireResponse): number {
  const times = [response.authored, response.meta?.lastUpdated]
    .map((value) => Date.parse(value ?? ''))
    .filter((value) => !Number.isNaN(value))
  return times.length ? Math.min(...times) : Number.NEGATIVE_INFINITY
}

function newestInsuranceRequest(responses: QuestionnaireResponse[]): QuestionnaireResponse | undefined {
  return responses.reduce<QuestionnaireResponse | undefined>(
    (newest, response) =>
      !newest || insuranceRequestSubmittedAt(response) > insuranceRequestSubmittedAt(newest)
        ? response
        : newest,
    undefined,
  )
}

export async function fetchLatestPatientInsuranceRequest(
  medplum: MedplumClient,
  patientId: string,
): Promise<PatientInsuranceRequest | null> {
  // The newest submission decides, as on the patient's Profile: once it is closed nothing is
  // open, and an older request never resurfaces. Submission time, not last update, because
  // closing an older request bumps its _lastUpdated past a newer submission.
  const responses = await medplum.searchResources(
    'QuestionnaireResponse',
    `subject=Patient/${patientId}&questionnaire=${encodeURIComponent(
      PATIENT_INSURANCE_REQUEST_QUESTIONNAIRE,
    )}&_sort=-authored&_count=20`,
  )
  const newest = newestInsuranceRequest(responses)
  const request = newest ? parsePatientInsuranceRequest(newest) : null

  return request && request.status !== 'stopped' && request.status !== 'entered-in-error'
    ? request
    : null
}

export async function dismissPatientInsuranceRequest(
  medplum: MedplumClient,
  request: PatientInsuranceRequest,
  options?: MedplumRequestOptions,
): Promise<QuestionnaireResponse> {
  return medplum.updateResource<QuestionnaireResponse>(
    { ...request.questionnaireResponse, status: 'stopped' },
    options,
  )
}

export type PatientInsuranceRequestCloseResult =
  | { outcome: 'closed' }
  /** Someone else closed it after it was loaded. */
  | { outcome: 'already-closed' }
  /** Still open with a newer version; `current` is undefined if re-reading it failed. */
  | { outcome: 'changed'; current: QuestionnaireResponse | undefined }

/**
 * Closes the request only if it is still the version staff are looking at. If-Match turns a
 * change made after it loaded (the patient correcting their answers, or staff closing it
 * elsewhere) into a 412 instead of an overwrite with the stale copy; the request is then re-read
 * to tell the two apart. Any other failure is thrown.
 */
export async function closePatientInsuranceRequest(
  medplum: MedplumClient,
  request: PatientInsuranceRequest,
): Promise<PatientInsuranceRequestCloseResult> {
  try {
    await dismissPatientInsuranceRequest(medplum, request, {
      headers: { 'If-Match': `W/"${request.questionnaireResponse.meta?.versionId}"` },
    })
    return { outcome: 'closed' }
  } catch (error) {
    if (!(error instanceof OperationOutcomeError && getStatus(error.outcome) === 412)) {
      throw error
    }
    // Someone else wrote it, so this client's cached searches still hold the loaded version; drop
    // them, or the caller's refresh shows the stale request again.
    medplum.invalidateSearches('QuestionnaireResponse')
    const current = await medplum
      .readResource('QuestionnaireResponse', request.id, { cache: 'no-cache' })
      .catch(() => undefined)
    const alreadyClosed = current?.status === 'stopped' || current?.status === 'entered-in-error'
    return alreadyClosed ? { outcome: 'already-closed' } : { outcome: 'changed', current }
  }
}

export async function resolvePatientInsuranceRequestImageUrls(
  medplum: MedplumClient,
  request: PatientInsuranceRequest | null,
): Promise<{ front: string | null; back: string | null }> {
  const getAttachmentUrl = async (reference?: string): Promise<string | null> => {
    if (!reference) {
      return null
    }

    const id = reference.startsWith('DocumentReference/') ? reference.split('/')[1] : reference
    if (!id) {
      return null
    }

    const document = await medplum.readResource('DocumentReference', id)
    return document.content?.find((content) => content.attachment?.url)?.attachment?.url || null
  }

  return {
    front: await getAttachmentUrl(request?.frontInsuranceCardDocumentReference),
    back: await getAttachmentUrl(request?.backInsuranceCardDocumentReference),
  }
}

/**
 * The organization the caller acts for — the clinic whose decisions they record. Picked exactly
 * as the middleware's resolveCallerOrganizationReference picks the clinic whose decisions the
 * insurance verdict reads (organizations/utils/resolve-caller-organization.ts), so a decision
 * saved here is the one insuranceVerdicts reports: the profile's organization compartment, else
 * the practitioner's most recently updated active role, else their most recent role.
 */
export async function resolveCurrentOrganizationId(
  medplum: MedplumClient,
  practitionerId?: string,
): Promise<string | null> {
  const compartmentOrganization = medplum
    .getProfile()
    ?.meta?.compartment?.find((item) => item.reference?.match(/organization/i))
    ?.reference?.split('/')[1]
  if (compartmentOrganization) {
    return compartmentOrganization
  }
  if (!practitionerId) {
    return null
  }

  const base = `practitioner=Practitioner/${practitionerId}`
  const role =
    (await medplum.searchOne('PractitionerRole', `${base}&active=true&_sort=-_lastUpdated`)) ??
    (await medplum.searchOne('PractitionerRole', `${base}&_sort=-_lastUpdated`))

  return role?.organization?.reference?.split('/').pop() || null
}
